import WbButton from '../../components/common/WbButton'
import CheckInInfo from './components/CheckInInfo'
import {useNavigate} from 'react-router-dom'
import paths from '../../routes/paths'
import React, {useEffect} from 'react'
import {
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Skeleton,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import checkinApi from '../../services/api/checkin/checkinApi'
import CheckInSurveyBlock from './components/CheckInSurveyBlock'
import {Refresh} from '@mui/icons-material'
import Question from './components/Question'
import PreviousCheckInList from './components/PreviousCheckInList'
import WbModal from '../../components/common/WbModal'
import CheckInQuestion from './components/CheckInQuestion'
import {getApiHookParams} from '../../auth'
import CheckInLifecycleActions from './components/CheckInLifecycleActions'
import {
  cycleCompletionSentence,
  cycleLengthSentence,
  frequencyLabel,
  launchCadenceSentence,
} from './checkInFrequency'
import {getLocalDate} from '../../utils/utilities'

export default function CheckIn() {
  const navigate = useNavigate()
  const apiParams = getApiHookParams()

  const [loading, setLoading] = React.useState(false)
  const [data, setData] = React.useState(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [showPrevious, setShowPrevious] = React.useState(false)

  const {isMat, groupId, schoolId} = apiParams
  const loadCheckIn = React.useCallback(() => {
    setLoading(true)
    setLoadFailed(false)
    checkinApi
      .get(isMat ? groupId : 0, isMat ? 0 : schoolId)
      .then(res => {
        setData(res && res.checkIn ? res : null)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching check-in surveys:', err)
        setData(null)
        setLoadFailed(true)
        setLoading(false)
      })
  }, [isMat, groupId, schoolId])

  useEffect(() => {
    loadCheckIn()
  }, [loadCheckIn])

  if (loading)
    return (
      <div className="white-container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <>
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </>
          </Grid>
        </Grid>
      </div>
    )
  if (loadFailed) {
    return (
      <div className="white-container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="error">
              We could not load your check-in surveys. Please try again.
            </Alert>
          </Grid>
          <Grid item xs={12} textAlign={'right'}>
            <WbButton CustomButtonText={'Retry'} onClick={loadCheckIn} />
          </Grid>
        </Grid>
      </div>
    )
  }
  if (!data || !data.checkIn) {
    return (
      <div className="white-container">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Alert severity="info">
              No active check-in survey suite found. Please create a new
              check-in survey suite to get started.
            </Alert>
          </Grid>
          <Grid item xs={12} textAlign={'right'}>
            <WbButton
              CustomButtonText={'Create Check-In Survey Suite'}
              onClick={e =>
                navigate(paths.checkinSurveysEdit.replace(':id', 0))
              }
            />
          </Grid>
        </Grid>
      </div>
    )
  }
  return (
    <div className="white-container">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <CheckInLifecycleActions
              checkIn={data.checkIn}
              onChanged={loadCheckIn}
            />
            <WbButton
              CustomButtonText={'Previous Check-In Surveys'}
              onClick={e => setShowPrevious(true)}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Accordion square disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              View Check-In Survey Settings
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <div>
                      Your current check-in survey suite is made up of these
                      surveys:
                    </div>
                    <WbButton
                      CustomButtonText={'Edit Check-In Surveys'}
                      // Always open the wizard on this check-in so its frequency, dates,
                      // holiday and email settings, templates, question and participants are
                      // there to change. Whether the edit updates the setup in place or ends
                      // it and starts a new one at Cycle 1 is the API's decision, made from
                      // whether instances have been generated; the page used to start a
                      // running check-in's edit from an empty wizard to force the latter.
                      onClick={e =>
                        navigate(
                          paths.checkinSurveysEdit.replace(':id', data?.checkIn.id)
                        )
                      }
                    />
                  </Stack>
                </Grid>

                <Grid item xs={12}>
                  <CheckInSurveyBlock checkin={data?.checkIn} />
                </Grid>
                <Grid item xs={12}>
                  <div className="checkin-survey-frequency">
                    <Refresh /> <strong>Check-in Surveys Frequency:</strong>{' '}
                    {frequencyLabel(data?.checkIn?.frequency)}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="success">
                    The questions in these surveys are split between each
                    check-in.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  {data?.groupCycleStatus === 2 ? (
                    <CheckInQuestion
                      canChange={false}
                      checkInQuestion={JSON.parse(data.checkIn.checkInQuestion)}
                    ></CheckInQuestion>
                  ) : (
                    <Question
                      question={{...data?.checkinQuestion}}
                      setQuestion={q => {
                        setData({...data, checkinQuestion: {...q}})
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Students will be offered the opportunity to start a safe
                    space conversation if they select the lowest two answer
                    options.
                  </Alert>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        <Grid item xs={12}>
          <CheckInInfo data={data} />
        </Grid>
        <Grid item xs={12}>
          <Alert severity="success">
            {launchCadenceSentence(data?.checkIn?.frequency)}
            <br /> {cycleLengthSentence(data?.instanceCount)}
            {data?.cycleEndDate && (
              <>
                <br />
                {cycleCompletionSentence(getLocalDate(data.cycleEndDate))}
              </>
            )}
          </Alert>
        </Grid>
      </Grid>

      {showPrevious && (
        <WbModal
          content={<PreviousCheckInList id={data.checkIn.id} />}
          title={'Previous Check-In Cycles'}
          onClose={e => setShowPrevious(false)}
        />
      )}
    </div>
  )
}
